import { useState } from 'react'
import { ActionInfo, Button, CheckboxField, ResourceSelectField, StatusMessage, TextField } from '@krp/ui-kit'
import { sourceOptions, type SourceKinds } from '../../../shared/sourceOptions'
import { auditDescription, problemText, type SalesPointProblem } from '../model/format'
import type { SalesPoint, SalesPointAudit, SalesPointDraft, SalesPointSource, SourceRegisters } from '../model/types'
import './sales-points.css'

/** Кассы одного источника: экран сам не ходит в сеть, строка источника просит их у привязки. */
export interface SourceRegistersLoad { registers: SourceRegisters | undefined; loading: boolean; error: SalesPointProblem | null }
export interface SalesPointEditorProps {
  /** Сохранённая группа; нет — создаётся новая. */
  point: SalesPoint | null
  /** Номер группы: кассы, занятые другой группой, выбрать нельзя. */
  pointId: string
  sources: readonly SalesPointSource[]
  kinds?: SourceKinds
  sourcesLoading: boolean
  audit: SalesPointAudit[] | undefined
  busy: boolean
  error: SalesPointProblem | null
  /** Кассы выбранного источника; вызывается как хук из строки источника. */
  useRegisters: (resourceId: string) => SourceRegistersLoad
  save: (draft: SalesPointDraft, archived: boolean) => void
  back: () => void
}

/** Одна строка «источник → кассы». */
interface SourceRow { id: number; resourceId: string; keys: string[] }
type Asking = 'save' | 'archive' | null

function SourceRowEditor(props: {
  row: SourceRow; index: number; pointId: string; point: SalesPoint | null; sources: readonly SalesPointSource[]; kinds: SalesPointEditorProps['kinds']; sourcesLoading: boolean; taken: ReadonlySet<string>
  useRegisters: SalesPointEditorProps['useRegisters']; change: (row: SourceRow) => void; remove: () => void
}) {
  const { row, index } = props
  const { registers, loading, error } = props.useRegisters(row.resourceId)
  const items = registers?.items ?? []
  const known = new Set(items.map((item) => item.key))
  // Касса, которую группа уже хранит, но источник сейчас не отдаёт: остаётся в группе, пока человек её не снимет.
  const lost = (props.point?.registers ?? []).filter((register) => register.resourceId === row.resourceId && !known.has(register.registerKey) && row.keys.includes(register.registerKey))
  const toggle = (key: string, checked: boolean) => props.change({ ...row, keys: checked ? [...row.keys, key] : row.keys.filter((item) => item !== key) })
  return <li className="sales-points__row">
    <ResourceSelectField label={`Источник ${index + 1}`} actionId="receipts.sales-points.source" value={row.resourceId}
      options={sourceOptions(props.sources.filter((source) => source.id === row.resourceId || !props.taken.has(source.id)), props.kinds)} notChosen="Выберите источник" loading={props.sourcesLoading}
      onChange={(resourceId) => props.change({ ...row, resourceId, keys: [] })} />
    {loading && row.resourceId !== '' && <p role="status">Загружаем кассы…</p>}
    {error !== null && <StatusMessage kind="error" title="Не удалось получить кассы источника" text={problemText(error)} />}
    {registers !== undefined && !registers.complete && <p role="status">Список касс неполный{registers.note === null ? '' : `: ${registers.note}`}</p>}
    {registers !== undefined && items.length === 0 && lost.length === 0 && <p role="status">В источнике нет касс.</p>}
    <ul className="sales-points__registers" aria-label={`Кассы источника ${index + 1}`}>
      {items.map((item) => {
        const note = [item.shopName, item.organizationName ?? '', item.archived ? 'отключена' : ''].filter((part) => part !== '').join(' · ')
        // Касса может входить в несколько магазинов (слово владельца 28.10.2026): подсказка только сообщает, где она уже есть.
        return <li key={item.key} className="sales-points__register">
          <CheckboxField actionId="receipts.sales-points.register" label={item.name === '' ? item.key : item.name} checked={row.keys.includes(item.key)}
            onChange={(checked) => toggle(item.key, checked)} />
          {note !== '' && <small>{note}</small>}
          {item.pointName !== null && <small>Уже в магазинах: {item.pointName}</small>}
        </li>
      })}
      {lost.map((register) => <li key={register.registerKey} className="sales-points__register">
        <CheckboxField actionId="receipts.sales-points.register" label={register.registerName === '' ? register.registerKey : register.registerName} checked onChange={(checked) => toggle(register.registerKey, checked)} />
        <small>Источник сейчас эту кассу не отдаёт</small>
      </li>)}
    </ul>
    <div><Button actionId="receipts.sales-points.remove-source" aria-label={`Убрать источник ${index + 1}`} onClick={props.remove}>Убрать</Button></div>
  </li>
}

/** Номера строк нужны только как ключи списка: счётчик общий, значение нигде не показывается. */
let rowSeq = 0
const nextRowId = () => { rowSeq += 1; return rowSeq }

function initialRows(point: SalesPoint | null, next: () => number): SourceRow[] {
  const byResource = new Map<string, string[]>()
  for (const register of point?.registers ?? []) byResource.set(register.resourceId, [...(byResource.get(register.resourceId) ?? []), register.registerKey])
  const rows = [...byResource.entries()].map(([resourceId, keys]) => ({ id: next(), resourceId, keys }))
  return rows.length > 0 ? rows : [{ id: next(), resourceId: '', keys: [] }]
}

/**
 * Настройка магазина: имя и пары «источник → кассы». Касса чужой группы серая, с именем этой группы, и не выбирается.
 * «Сохранить» и «В архив» спрашивают «Вы уверены?»; отказ службы показан дословно, с кодом.
 */
export function SalesPointEditorScreen(props: SalesPointEditorProps) {
  const nextId = nextRowId
  const [name, setName] = useState(props.point?.name ?? '')
  const [rows, setRows] = useState<SourceRow[]>(() => initialRows(props.point, nextId))
  const [asking, setAsking] = useState<Asking>(null)
  const registers = rows.flatMap((row) => row.resourceId === '' ? [] : row.keys.map((registerKey) => ({ resourceId: row.resourceId, registerKey })))
  const ready = name.trim() !== '' && registers.length > 0
  const stored = props.point !== null && props.point.version > 0
  const commit = (done: Asking) => { setAsking(null); props.save({ name: name.trim(), registers }, done === 'archive') }
  const takenSources = (row: SourceRow) => new Set(rows.flatMap((other) => other.id === row.id || other.resourceId === '' ? [] : [other.resourceId]))
  return <section className="sales-points" aria-label="Настройка магазина">
    {props.error !== null && <StatusMessage kind="error" title="Не удалось сохранить магазин" text={problemText(props.error)} />}
    <div className="sales-points__toolbar"><h3>{stored ? 'Настройка магазина' : 'Новый магазин'}</h3>
      <Button actionId="receipts.sales-points.back" disabled={props.busy} onClick={props.back}>К списку магазинов</Button></div>
    <fieldset disabled={props.busy}>
      <p>Выберите кассы разных источников: статистика магазина сведёт их продажи. Одна касса может быть только в одном магазине.</p>
      <TextField label="Название магазина" actionId="receipts.sales-points.name" value={name} onChange={setName} />
      <ul className="sales-points__registers" aria-label="Источники магазина">{rows.map((row, index) => <SourceRowEditor key={row.id} row={row} index={index} pointId={props.pointId} point={props.point}
        sources={props.sources} kinds={props.kinds} sourcesLoading={props.sourcesLoading} taken={takenSources(row)} useRegisters={props.useRegisters}
        change={(changed) => setRows(rows.map((item) => item.id === changed.id ? changed : item))}
        remove={() => setRows(rows.length > 1 ? rows.filter((item) => item.id !== row.id) : [{ id: nextId(), resourceId: '', keys: [] }])} />)}</ul>
      <div className="sales-points__toolbar">
        <Button actionId="receipts.sales-points.add-source" onClick={() => setRows([...rows, { id: nextId(), resourceId: '', keys: [] }])}>+ Источник</Button>
      </div>
      {!ready && <p role="status">Нужны название и хотя бы одна касса.</p>}
      <div className="sales-points__toolbar">
        <Button variant="primary" actionId="receipts.sales-points.save" busy={props.busy && asking === null} disabled={!ready} onClick={() => setAsking('save')}>Сохранить</Button>
        {stored && <Button variant="danger" actionId="receipts.sales-points.archive" disabled={props.busy} onClick={() => setAsking('archive')}>В архив</Button>}
      </div>
      {asking !== null && <div className="sales-points__confirmation" role="alertdialog" aria-label="Подтверждение">
        <h4>Вы уверены?</h4>
        <p>{asking === 'archive' ? 'Магазин уйдёт в архив: его кассы освободятся, статистики магазина больше не будет. Чеки в источниках не изменятся.' : 'Состав магазина сохранится в нашей базе, изменение попадёт в историю. Чеки в источниках не изменятся.'}</p>
        {asking === 'archive'
          ? <Button variant="danger" actionId="receipts.sales-points.confirm-archive" busy={props.busy} onClick={() => commit('archive')}>Да, в архив</Button>
          : <Button variant="primary" actionId="receipts.sales-points.confirm-save" busy={props.busy} onClick={() => commit('save')}>Да, сохранить</Button>}
        <Button actionId="receipts.sales-points.cancel" disabled={props.busy} onClick={() => setAsking(null)}>Отмена</Button>
      </div>}
    </fieldset>
    {stored && <ActionInfo id="receipts.sales-points.audit" layout="block"><details><summary>История изменений</summary>
      {props.audit === undefined ? <p role="status">Загружаем историю…</p> : props.audit.length === 0 ? <p>Изменений пока нет.</p> : <ol>{props.audit.map((item) => <li key={item.operationId}>
        <p>{new Date(item.at).toLocaleString('ru-RU')} · {item.actor}</p><p>Было: {auditDescription(item.before)}</p><p>Стало: {auditDescription(item.after)}</p>
      </li>)}</ol>}
    </details></ActionInfo>}
  </section>
}
